import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImageDown, Download } from "lucide-react";
import { Seo } from "@/components/Seo";
import { GenericToolShell } from "@/components/GenericToolShell";
import { SeoSections } from "@/components/SeoSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IMAGE_VARIANTS } from "@/data/imageVariants";

const PRESETS = {
  "square-1000": { label: "Square 1000 × 1000", w: 1000, h: 1000 },
  "square-1080": { label: "Square 1080 × 1080", w: 1080, h: 1080 },
  "square-800": { label: "Square 800 × 800", w: 800, h: 800 },
  "portrait-1080": { label: "Portrait 1080 × 1350", w: 1080, h: 1350 },
  "custom": { label: "Custom", w: 1000, h: 1000 },
};

export default function ImageResizer({ variant }) {
  const v = variant ? IMAGE_VARIANTS[variant] : null;
  const initialPreset = v?.defaultPreset || "square-1000";
  const [img, setImg] = useState(null); // {el, name, w, h, url}
  const [preset, setPreset] = useState(initialPreset);
  const [w, setW] = useState(PRESETS[initialPreset].w);
  const [h, setH] = useState(PRESETS[initialPreset].h);
  const [keepAspect, setKeepAspect] = useState(true);
  const [format, setFormat] = useState("image/jpeg");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please choose an image file (JPG, PNG, WebP)."); return; }
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => setImg({ el, name: file.name, w: el.naturalWidth, h: el.naturalHeight, url });
    el.onerror = () => toast.error("Could not read this image.");
    el.src = url;
  };

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== "custom") { setW(PRESETS[key].w); setH(PRESETS[key].h); }
  };

  const resize = () => {
    if (!img) { toast.error("Upload an image first."); return; }
    setBusy(true);
    try {
      let tw = Number(w), th = Number(h);
      if (keepAspect) {
        const r = img.w / img.h;
        if (tw / th > r) tw = Math.round(th * r); else th = Math.round(tw / r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = tw; canvas.height = th;
      const ctx = canvas.getContext("2d");
      if (format === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, tw, th); }
      ctx.drawImage(img.el, 0, 0, tw, th);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const ext = format === "image/png" ? "png" : format === "image/webp" ? "webp" : "jpg";
        const a = document.createElement("a"); a.href = url; a.download = `ecroply-${tw}x${th}.${ext}`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        toast.success("Resized image downloaded!");
        setBusy(false);
      }, format, 0.92);
    } catch { toast.error("Could not resize the image."); setBusy(false); }
  };

  const seoTitle = v ? `${v.seoTitle} | Ecroply AI` : "Product Image Resizer — Resize Images Online Free | Ecroply AI";
  const seoDesc = v?.seoDesc || "Resize product images to marketplace sizes or a custom size, in your browser. Supports JPG, PNG and WebP. Free, no upload, no login.";
  const seoPath = v?.path || "/tools/product-image-resizer";
  const title = v?.title || "Product Image Resizer";
  const promise = v?.promise || "Resize product images to preset or custom sizes in your browser.";
  const jsonLd = v ? [{
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: v.jsonName,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Any (runs in browser)",
    offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
    url: `https://ecroplyai.in${v.path}`,
  }] : undefined;

  return (
    <>
      <Seo title={seoTitle} description={seoDesc} path={seoPath} faq={v?.content?.faq} jsonLd={jsonLd}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Ecommerce Tools", path: "/ecommerce-tools" }, { name: title, path: seoPath }]} />
      <GenericToolShell icon="ImageDown" title={title} promise={promise}
        privacy="Your file is processed in your browser on this device and is not uploaded to a server.">
        {!img ? (
          <div
            role="button" tabIndex={0}
            onClick={() => inputRef.current?.click()}
            data-testid="image-dropzone"
            className="border-2 border-dashed border-blue-300 bg-blue-50/50 hover:border-blue-500 hover:bg-blue-50 rounded-2xl p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-all"
          >
            <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20"><ImageDown className="w-7 h-7 text-white" /></div>
            <p className="font-heading font-bold text-slate-900 text-lg">Upload an image</p>
            <p className="text-sm text-slate-500 mt-1">JPG, PNG or WebP</p>
            <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="image-input" onChange={(e) => e.target.files[0] && load(e.target.files[0])} />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-center justify-center">
              <img src={img.url} alt={img.name} className="max-h-64 rounded-lg" data-testid="image-preview" />
            </div>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Original: {img.w} × {img.h}px</p>
              <div>
                <Label className="text-sm font-semibold">Preset</Label>
                <Select value={preset} onValueChange={applyPreset}>
                  <SelectTrigger className="mt-1.5" data-testid="select-preset"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRESETS).map(([k, val]) => <SelectItem key={k} value={k}>{val.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <div><Label className="text-xs">Width (px)</Label><Input type="number" value={w} onChange={(e) => { setW(e.target.value); setPreset("custom"); }} data-testid="input-width" /></div>
                <div><Label className="text-xs">Height (px)</Label><Input type="number" value={h} onChange={(e) => { setH(e.target.value); setPreset("custom"); }} data-testid="input-height" /></div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Keep aspect ratio</Label>
                <Switch checked={keepAspect} onCheckedChange={setKeepAspect} data-testid="switch-aspect" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger className="mt-1.5" data-testid="select-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image/jpeg">JPG</SelectItem>
                    <SelectItem value="image/png">PNG</SelectItem>
                    <SelectItem value="image/webp">WebP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={resize} disabled={busy} className="flex-1 h-11 bg-blue-600 hover:bg-blue-700" data-testid="resize-button">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Resize & download</>}
                </Button>
                <Button variant="outline" onClick={() => setImg(null)} data-testid="image-reset">Change</Button>
              </div>
            </div>
          </div>
        )}
      </GenericToolShell>

      {v && (
        <SeoSections
          intro={v.content.intro}
          howTo={v.content.howTo}
          requirements={v.content.requirements}
          faq={v.content.faq}
          links={v.content.links}
        />
      )}
    </>
  );
}
